import {generateAppId, registerAppId} from '@github-ui/app-uuid'
import {noop} from '@github-ui/noop'
import {getQueryClient} from '@github-ui/query-client'
import {createReactRootErrorHandlers} from '@github-ui/react-core/react-root-error-handlers'
import ReactProfilingMode from '@github-ui/react-profiling-mode'
import {sendCustomMetric, updateCurrentApp} from '@github-ui/stats'
import {type DetailedHTMLProps, type HTMLAttributes, version} from 'react'
import {createRoot, type Root} from 'react-dom/client'

import Memex from './memex'
import {renderMemex} from './render-memex'
import {PROJECT_ROUTE} from './routes'

const MEMEX_APP_NAME = 'memex'

class ProjectsV2 extends HTMLElement {
  declare reactRoot: Root | undefined
  declare uuid: string

  // Deliberately synchronous. Profiling builds are selected by the server-rendered import map,
  // which remaps the `react-dom/client` specifier itself, so there is nothing to await here.
  connectedCallback() {
    updateCurrentApp(MEMEX_APP_NAME, false)
    const errorHandlers = createReactRootErrorHandlers({appName: MEMEX_APP_NAME})
    const reactRoot = createRoot(this, errorHandlers)
    this.reactRoot = reactRoot
    const match = PROJECT_ROUTE.matchFullPathOrChildPaths(window.location.pathname)
    if (!match) return

    this.uuid = generateAppId()
    registerAppId(this.uuid)
    const renderStartMark = performance.mark(`react-root:render-start:${MEMEX_APP_NAME}`)
    const collectRenderPerformanceStats = this.#startInitialRenderPerformanceMeasure(renderStartMark)
    renderMemex(<Memex rootElement={this} />, this, reactRoot, collectRenderPerformanceStats)
  }

  disconnectedCallback() {
    getQueryClient().removeQueries({queryKey: ['memex']})
    this.reactRoot?.unmount()
  }

  #sendHydrationMetric({requestUrl, duration}: {requestUrl: string; duration: number}) {
    sendCustomMetric({
      requestUrl,
      name: 'BROWSER_REACT_HYDRATION_DURATION',
      value: duration,
      tags: {
        appName: MEMEX_APP_NAME,
        reactVersion: version,
        renderType: 'createRoot',
        subAppName: MEMEX_APP_NAME,
        profilingEnabled: String(ReactProfilingMode.isEnabled()),
        profilingSource: ReactProfilingMode.getDecisionSource(),
      },
    })
  }

  /**
   * @returns A function that, when called, will emit stats to track the initial render duration.
   */
  #startInitialRenderPerformanceMeasure(startMark: PerformanceMark): () => void {
    try {
      const requestUrl = window.location.href
      return () => {
        const duration = window.performance.now() - startMark.startTime
        this.#sendHydrationMetric({requestUrl, duration})
      }
    } catch {
      // silently fail
      return noop
    }
  }
}

function register() {
  if (typeof window === 'undefined') return
  if (!window.customElements.get('projects-v2')) {
    // eslint-disable-next-line wc/tag-name-matches-class
    window.customElements.define('projects-v2', ProjectsV2)
  }
}

declare module 'react' {
  namespace JSX {
    interface IntrinsicElements {
      'projects-v2': DetailedHTMLProps<HTMLAttributes<ProjectsV2>, ProjectsV2>
    }
  }
}

register()
